import {Observable, from, firstValueFrom} from "rxjs"
import {ReifiedMeshData, importMeshesFromFBXFile, ReifiedGeometryData, RawPolyMeshData} from "@cm/template-nodes"
import {WebAssemblyWorkerService} from "@app/template-editor/services/webassembly-worker.service"
import {createTaskOperator} from "@common/helpers/tasks/observable-task-manager"
import {MeshNodes, DataNodes} from "@cm/render-nodes"
import type {CmLogger} from "@cm/log"

export {WebAssemblyWorkerService}

export type ConvertedDracoData = {
    name?: string
    exporterVersion: string
    hasOriginalUvs: boolean
    drcData: Uint8Array
    defaultPosition: [number, number, number]
    centered: boolean
    dracoBitDepth: number
    dracoResolution: number
}

export type ConvertedDracoAndPLYData = ConvertedDracoData & {
    plyData: Uint8Array
}

export function compressMeshGLTF(
    workerService: WebAssemblyWorkerService,
    positionAttr: Float32Array,
    normalAttr: Float32Array,
    uvAttr: Float32Array,
    indices: Uint32Array,
    bitDepth: number,
): Observable<Uint8Array> {
    return workerService
        .invokeFunction<Uint8Array>("compressDracoFileGLTF", [positionAttr, normalAttr, uvAttr, indices, bitDepth])
        .pipe(createTaskOperator("compressMesh"))
}

export type MeshConversionOptions = {
    consolidate: boolean
    resolution: number
    center: boolean
    scaleFactor?: number
    scaleUv0?: boolean
    convertToWorldSpaceUvs: boolean
}

export function convertRawPolyMeshToDracoAndPLY(
    workerService: WebAssemblyWorkerService,
    rawPolyMeshData: RawPolyMeshData,
    options: MeshConversionOptions,
): Observable<ConvertedDracoAndPLYData> {
    return workerService
        .invokeFunction<ConvertedDracoAndPLYData>("convertRawPolyMeshToDracoAndPLY", [rawPolyMeshData, options])
        .pipe(createTaskOperator("convertRawPolyMeshToDracoAndPLY"))
}

export function convertOBJToDracoAndPLY(
    workerService: WebAssemblyWorkerService,
    buffer: ArrayBuffer,
    options: MeshConversionOptions,
): Observable<ConvertedDracoAndPLYData[]> {
    return workerService
        .invokeFunction<ConvertedDracoAndPLYData[]>("convertOBJToDracoAndPLY", [buffer, options])
        .pipe(createTaskOperator("convertOBJToDracoAndPLY"))
}

export function convertPLYToDracoAndPLY(
    workerService: WebAssemblyWorkerService,
    buffer: ArrayBuffer,
    options: MeshConversionOptions,
): Observable<ConvertedDracoAndPLYData> {
    return workerService
        .invokeFunction<ConvertedDracoAndPLYData>("convertPLYToDracoAndPLY", [buffer, options])
        .pipe(createTaskOperator("convertPLYToDracoAndPLY"))
}

export function convertFBXToDracoAndPLY(
    workerService: WebAssemblyWorkerService,
    buffer: ArrayBuffer,
    options: MeshConversionOptions,
    logger: CmLogger,
): Observable<ConvertedDracoAndPLYData[]> {
    const allMeshData = importMeshesFromFBXFile(new Uint8Array(buffer), logger)
    return from(
        (async (): Promise<ConvertedDracoAndPLYData[]> => {
            const results: ConvertedDracoAndPLYData[] = []
            for (const meshData of allMeshData) {
                const result = await firstValueFrom(
                    convertRawPolyMeshToDracoAndPLY(
                        workerService,
                        {
                            polyVertexCount: meshData.polyVertexCount,
                            position: meshData.position,
                            normal: meshData.normal,
                            materialID: meshData.materialID,
                            uvs: meshData.uvs?.map((uv) => uv),
                        },
                        options,
                    ),
                )
                results.push({...result, name: meshData.name})
            }
            return results
        })(),
    )
}

export function evaluateMesh(
    workerService: WebAssemblyWorkerService,
    graph: MeshNodes.Mesh,
    resources: DataNodes.EvaluationResources,
): Observable<ReifiedMeshData> {
    return workerService.invokeFunction<ReifiedMeshData>("evaluateMesh", [graph, resources]).pipe(createTaskOperator("evaluateMesh"))
}

export function evaluateRawPolyMesh(
    workerService: WebAssemblyWorkerService,
    graph: MeshNodes.Mesh,
    resources: DataNodes.EvaluationResources,
): Observable<RawPolyMeshData> {
    return workerService.invokeFunction<RawPolyMeshData>("evaluateRawPolyMesh", [graph, resources]).pipe(createTaskOperator("evaluateRawPolyMesh"))
}

export function evaluateGeometry(
    workerService: WebAssemblyWorkerService,
    graph: MeshNodes.GeomGraph,
    resources: DataNodes.EvaluationResources,
): Observable<ReifiedGeometryData> {
    return workerService.invokeFunction<ReifiedGeometryData>("evaluateGeometry", [graph, resources]).pipe(createTaskOperator("evaluateGeometry"))
}

export function clipAndOffsetMeshForDecal(
    workerService: WebAssemblyWorkerService,
    meshData: ReifiedMeshData,
    uvCenter: [number, number],
    uvRotation: number,
    size: [number, number],
    offset: number,
): Observable<ReifiedMeshData> {
    return workerService
        .invokeFunction<ReifiedMeshData>("clipAndOffsetMeshForDecal", [meshData, uvCenter, uvRotation, size, offset])
        .pipe(createTaskOperator("clipAndOffsetMeshForDecal"))
}
